import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Button,
  TextField,
  Chip,
  Typography
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS } from '../../theme/fordTheme.js';

// Common style patterns to reduce duplication
const COMMON_DEVICE_TROUBLE_STYLES = {
  baseContainer: {
    // backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    // border: `1px solid ${FORD_COLORS.primaryLight}`,
    borderRadius: FORD_RADIUS.lg,
    padding: FORD_SPACING.lg,
  },
  
  buttonBase: {
    textTransform: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontWeight: '500',
  },
  
  inputBase: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: FORD_COLORS.backgroundford,
      color: FORD_COLORS.textLight,
      borderRadius: '12px',
      fontSize: FORD_TYPOGRAPHY.body2,
      '& fieldset': {
        borderColor: '#233058',
      },
      '&:hover fieldset': {
        borderColor: '#4f7cff',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4f7cff',
      },
    },
    '& .MuiInputLabel-root': {
      color: FORD_COLORS.textMuted,
      fontSize: FORD_TYPOGRAPHY.body2,
      '&.Mui-focused': {
        color: '#4f7cff',
      },
    },
  },
  
  chipColors: {
    online: {
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      color: '#22c55e',
      border: '1px solid rgba(34, 197, 94, 0.3)',
    },
    offline: {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.3)',
    },
    default: {
      backgroundColor: 'rgba(156, 163, 199, 0.2)',
      color: '#9aa3c7',
      border: '1px solid rgba(156, 163, 199, 0.3)',
    }
  },
  
  actionButtonBase: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    padding: '4px',
    borderRadius: '6px',
    border: '1px solid',
    fontSize: '14px',
    '& .MuiButton-startIcon': {
      margin: 0,
    },
  },
  
  centeredContainer: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  }
};

// Helper function to get chip styles
const getDeviceTroubleChipStyles = (label) => {
  switch (label) {
    case 'online':
      return COMMON_DEVICE_TROUBLE_STYLES.chipColors.online;
    case 'offline':
      return COMMON_DEVICE_TROUBLE_STYLES.chipColors.offline;
    default:
      return COMMON_DEVICE_TROUBLE_STYLES.chipColors.default;
  }
};

// Main container - DeviceTrouble specific
export const DeviceTroubleContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: FORD_COLORS.backGroundColor,
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  padding: '28px',
  // border: `1px solid ${FORD_COLORS.primaryLight}`,
  // borderRadius: FORD_RADIUS.sm,
  width: '100%',
  // boxSizing: 'border-box',
  overflow: 'hidden',
}));

// Outer container - DeviceTrouble specific
export const DeviceTroubleOuterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100vw',
  margin: '0 auto',
  padding: '0',
  // boxSizing: 'border-box',
  // overflow: 'hidden',
}));

// Page header - DeviceTrouble specific
export const DeviceTroublePageHeader = styled(Box)(({ theme }) => ({
 marginBottom: '22px',
  '& h2': {
    // margin: 0,
    // marginTop: '10px',
    // marginBottom: '8px',
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    color: 'rgb(19, 34, 52)',
    // margin: `${FORD_SPACING.sm} 0`,
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    marginTop: '4px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
}));

// Panel container - DeviceTrouble specific
export const DeviceTroublePanel = styled(Paper)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.baseContainer,
    border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
   '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    marginTop: '4px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '600'
  }

}));

// Search panel - special styling for search form
export const DeviceTroubleSearchPanel = styled(DeviceTroublePanel)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  marginBottom: '16px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  flexWrap: 'wrap',
   border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
  },
}));


// Search input field - DeviceTrouble specific
export const DeviceTroubleSearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  ...COMMON_DEVICE_TROUBLE_STYLES.inputBase,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .MuiOutlinedInput-input': {
    color: FORD_COLORS.textLight,
    padding: '10px 12px',
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '400',
  },
  '& .MuiInputLabel-root': {
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '400',
  },
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
  },
}));

// Primary button - DeviceTrouble specific
export const DeviceTroublePrimaryButton = styled(Button)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.buttonBase,
  backgroundColor: '#066fef',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    backgroundColor: '#233058',
    color: '#9aa3c7',
  },
}));

// Secondary button - DeviceTrouble specific
export const DeviceTroubleSecondaryButton = styled(Button)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.buttonBase,
  border: '1px solid #233058',
  '&:hover': {
    backgroundColor: 'rgba(79, 124, 255, 0.1)',
    borderColor: '#4f7cff',
  },
  '&:disabled': {
    backgroundColor: 'transparent',
    color: '#9aa3c7',
    borderColor: '#233058',
  },
}));

// Layout container for search results and diagnostic console - DeviceTrouble specific
export const DeviceTroubleLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: FORD_SPACING.md,
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  marginTop: '16px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: FORD_SPACING.md,
  },
}));

// Search results area - left side
export const DeviceTroubleSearchResults = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  maxWidth: '70%',
  // overflow: 'hidden',
    // border: 'none',
  // boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)', 
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Diagnostic console area - right side
export const DeviceTroubleConsoleArea = styled(Box)(({ theme }) => ({
  width: '30%',
  minWidth: '30%',
  flexShrink: 0,
  maxWidth: '30%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    minWidth: '100%',
    maxWidth: '100%',
  },
}));

// Table container - DeviceTrouble specific
export const DeviceTroubleTableContainer = styled(DeviceTroublePanel)(({ theme }) => ({
  marginTop: 16,
  padding: 0,
  borderRadius: '0px', // Rounded corners
  // border: '1px solid #e0e6ed', // Light border
  overflow: 'hidden',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', // Subtle shadow
  '& .MuiTable-root': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    border: `1px solid ${FORD_COLORS.primaryLight}`,
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      fontFamily: FORD_TYPOGRAPHY.newfont,
    },
    '& th': {
      backgroundColor: 'rgb(30, 45, 90)', // Solid dark blue background
      color: '#ffffff', // Pure white text for better contrast
      fontWeight: '700', // Bolder font weight
      padding: '2px 12px', // More generous padding
      textAlign: 'left',
      borderBottom: '1px solid #233058',
      fontSize: '12px', // Slightly smaller, cleaner font
      letterSpacing: '0.5px', // Letter spacing for readability
      textTransform: 'uppercase', // Uppercase headers for professionalism
      position: 'sticky',
      top: 0,
      zIndex: 11,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)', // Stronger shadow
      fontFamily: FORD_TYPOGRAPHY.newfont,
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1,
    },
    '& td': {
      padding: '2px 12px', // Match header padding for consistency
      borderBottom: '1px solid rgba(35, 48, 88, 0.3)',
      color: 'rgb(19, 34, 52)', // Darker, more readable text
      fontSize: '14px', // Slightly larger for better readability
      // fontWeight: '500', // Medium font weight
      backgroundColor: '#ffffff', // Clean white background
      fontFamily: FORD_TYPOGRAPHY.newfont,
      lineHeight: '1.4', // Better line spacing
      verticalAlign: 'middle', // Center content vertically
    },
    '& tbody tr:hover': {
      backgroundColor: 'rgba(79, 124, 255, 0.08)', // Subtle hover effect
    },
    '& tbody tr:nth-of-type(even)': {
      backgroundColor: '#f8f9fa', // Alternating row colors
    }
  },
}));

// Status chip - DeviceTrouble specific
export const DeviceTroubleStatusChip = styled(Chip)(({ theme, label }) => ({
  ...getDeviceTroubleChipStyles(label),
  fontSize: '0.75rem',
  height: '24px',
  borderRadius: '12px',
  fontWeight: '500',
}));

// Console panel - special styling for diagnostic console
export const DeviceTroubleConsolePanel = styled(DeviceTroublePanel)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'fit-content',
  minHeight: '400px',
  [theme.breakpoints.down('md')]: {
    marginTop: '16px',
    minHeight: '300px',
  },
}));

// Console body - the scrollable log area
export const DeviceTroubleConsoleBody = styled(Box)(({ theme }) => ({
  border: '1px solid #233058',
  borderRadius: '10px',
  minHeight: '200px',
  maxHeight: '300px',
  height: 'auto',
  overflow: 'auto',
  padding: '12px',
  marginBottom: '12px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    minHeight: '150px',
    maxHeight: '250px',
  },
}));

// Console pre - for log text
export const DeviceTroubleConsolePre = styled('pre')(({ theme }) => ({
  margin: 0,
  fontFamily: FORD_TYPOGRAPHY.monoFont,
  fontSize: '11px',
  lineHeight: 1.4,
  color: FORD_COLORS.textLight,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '& div': {
    marginBottom: '2px',
    wordWrap: 'break-word',
  },
}));

// Console status bar
export const DeviceTroubleConsoleStatus = styled(Box)(({ theme }) => ({
  borderTop: '1px solid #233058',
  paddingTop: '6px',
  fontSize: FORD_TYPOGRAPHY.caption,
  letterSpacing: '0.2px',
  color: FORD_COLORS.textLight,
  '& .muted': {
    color: FORD_COLORS.textMuted,
  },
}));

// No results message
export const DeviceTroubleNoResults = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  color: FORD_COLORS.textMuted,
  padding: '20px',
  fontSize: FORD_TYPOGRAPHY.body2,
}));

// Panel title text
export const DeviceTroublePanelTitle = styled('span')(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: FORD_TYPOGRAPHY.caption,
  fontWeight: 500,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
}));

// Panel subtitle text
export const DeviceTroublePanelSubtitle = styled('span')(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: FORD_TYPOGRAPHY.body2,
}));

// Muted text for console status
export const DeviceTroubleMutedText = styled('span')(({ theme }) => ({
  color: FORD_COLORS.textMuted,
}));

// Loading message - DeviceTrouble specific
export const DeviceTroubleLoadingMessage = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.centeredContainer,
}));

// Error message - DeviceTrouble specific
export const DeviceTroubleErrorMessage = styled(Typography)(({ theme }) => ({
  color: '#ef4444',
  textAlign: 'center',
  fontSize: '1rem',
}));

// Action icons container - DeviceTrouble specific
export const DeviceTroubleActionIcons = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.flexContainer,
  gap: '6px',
  justifyContent: 'left',
}));

// Helper functions for action button colors
const getActionColor = (actiontype) => {
  switch (actiontype) {
    case 'on': return '#10b981';
    case 'off': return '#b81111ff';
    default: return '#4f7cff';
  }
};

const getActionHoverColor = (actiontype) => {
  switch (actiontype) {
    case 'on': return '#059669';
    case 'off': return '#8b0909ff';
    default: return '#3b82f6';
  }
};

// Action icon button - DeviceTrouble specific
export const DeviceTroubleActionIconButton = styled(Button)(({ theme, actiontype }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.actionButtonBase,
  display: actiontype === 'on' || actiontype === 'off' ? 'none' : '',
  backgroundColor: getActionColor(actiontype),
  borderColor: getActionColor(actiontype),
  color: '#ffffff',
  '&:hover': {
    backgroundColor: getActionHoverColor(actiontype),
    borderColor: getActionHoverColor(actiontype),
  },
  '&:disabled': {
    backgroundColor: '#6b7280',
    borderColor: '#6b7280',
    color: '#9ca3af',
  },
}));

// History icon button - DeviceTrouble specific
export const DeviceTroubleHistoryIconButton = styled(Button)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.actionButtonBase,
  backgroundColor: 'transparent',
  borderColor: '#6b7280',
  color: '#6b7280',
  '&:hover': {
    backgroundColor: '#f3f4f6',
    borderColor: '#4b5563',
    color: '#4b5563',
  },
  '&:disabled': {
    backgroundColor: '#f9fafb',
    borderColor: '#d1d5db',
    color: '#d1d5db',
  },
}));

// History panel container - DeviceTrouble specific
export const DeviceTroubleHistoryPanel = styled(Box)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundLight,
  border: `1px solid ${FORD_COLORS.primaryLight}`,
  borderRadius: FORD_RADIUS.sm,
  padding: FORD_SPACING.md,
  marginTop: FORD_SPACING.md,
  width: '100%',
}));

// History panel header - DeviceTrouble specific
export const DeviceTroubleHistoryHeader = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.flexContainer,
  justifyContent: 'space-between',
  marginBottom: FORD_SPACING.sm,
  '& .title': {
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontWeight: '600'
  }
}));

// History table container - DeviceTrouble specific
export const DeviceTroubleHistoryTableContainer = styled(Box)(({ theme }) => ({
   fontSize: '12px',
   fontWeight: FORD_TYPOGRAPHY.newfont,
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: FORD_COLORS.backgroundLight,
    // borderRadius: FORD_RADIUS.sm,
    overflow: 'hidden',
    border: `1px solid ${FORD_COLORS.primaryLight}`,
     fontSize: '12px',
  },
  '& th': {
    backgroundColor: FORD_COLORS.backgroundDark,
    color: FORD_COLORS.textLight,
    padding: '2px 12px',
    textAlign: 'left',
    fontWeight: FORD_TYPOGRAPHY.bold,
    fontSize: '12px',
    borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
  },
  '& td': {
    padding: '2px 12px',
    borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
    color: FORD_COLORS.textLight,
    fontSize: '12px !important',
    verticalAlign: 'top',
  },
  '& tbody tr:hover': {
    backgroundColor: 'rgba(79, 124, 255, 0.05)',
  },
  '& tbody tr:last-child td': {
    borderBottom: 'none',
  },
}));

// History loading/error states - DeviceTrouble specific
export const DeviceTroubleHistoryMessage = styled(Box)(({ theme }) => ({
  ...COMMON_DEVICE_TROUBLE_STYLES.centeredContainer,
  padding: '40px 20px',
  color: FORD_COLORS.textMuted,
  gap: '12px',
}));
